import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { CreditCard, Smartphone, Banknote, TrendingUp, MapPin, Clock, AlertCircle } from 'lucide-react';
import { REPORT_SHIFTS } from '../constants/reportFilters';
import { reportsService } from '../services/reportsService';
import { useAuth } from '@/context/AuthContext';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';

interface PaymentMethodFilters {
  locationId: string;
  shift: string;
  dateFrom: string;
  dateTo: string;
}


interface PaymentMethodData {
  modalidad: string;
  displayName: string;
  ventas: number;
  transacciones: number;
  percentage: number;
  averageTicket: number;
  color: string;
}

export const PaymentMethodReport: React.FC = () => {
  const { user } = useAuth();
  const { quickRange, dateFrom, dateTo, setQuickRange, setManualDates } = useDateRangeDefaults('paymentMethodReport');
  const [filters, setFilters] = useState<PaymentMethodFilters>({
    locationId: 'all',
    shift: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodData[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setFilters(prev => ({ ...prev, locationId: defaultLocationId }));
    }
  }, [defaultLocationId]);

  const updateFilter = (key: keyof PaymentMethodFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const loadReportData = async () => {
    if (!filters.locationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await reportsService.getPaymentMethodsReport(filters.locationId, {
        startDate: filters.dateFrom || undefined,
        endDate: filters.dateTo || undefined,
        shift: filters.shift !== 'all' ? filters.shift : undefined,
        includeDetail: false
      });

      if (response.success) {
        setPaymentMethods(response.data.paymentMethods);
        setSummary(response.data.summary);
      } else {
        setError(response.error || 'Error al cargar datos');
      }
    } catch (err) {
      setError('Error al conectar con el servidor');
      console.error('Error loading payment methods report:', err);
    } finally {
      setLoading(false);
    }
  };




  // Sync date range with filters
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      dateFrom: dateFrom,
      dateTo: dateTo
    }));
  }, [dateFrom, dateTo]);

  const handleDateChange = (key: string, value: string) => {
    if (key === 'dateFrom') {
      setManualDates(value, dateTo);
      updateFilter('dateFrom', value);
    } else if (key === 'dateTo') {
      setManualDates(dateFrom, value);
      updateFilter('dateTo', value);
    }
  };


  const getPaymentIcon = (modalidad: string) => {
    switch (modalidad.toLowerCase()) {
      case 'efectivo':
        return <Banknote className="w-5 h-5" />;
      case 'tarjeta':
        return <CreditCard className="w-5 h-5" />;
      case 'aplicativo':
        return <Smartphone className="w-5 h-5" />;
      default:
        return <TrendingUp className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reporte por Modalidad de Venta</h2>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select value={filters.locationId} onValueChange={(value) => updateFilter('locationId', value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Rango</Label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="paymentMethod"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => updateFilter('dateFrom', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => updateFilter('dateTo', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Turno</Label>
              <Select value={filters.shift} onValueChange={(value) => updateFilter('shift', value)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_SHIFTS.map(shift => (
                    <SelectItem key={shift.id} value={shift.id}>{shift.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={loadReportData}
              disabled={loading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {loading ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
              <div>
                <p className="font-medium text-red-900">Error al cargar datos</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumen por modalidades */}
      {paymentMethods.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {paymentMethods.map((modalidad, index) => (
            <Card key={index} className="border-2" style={{ borderColor: modalidad.color + '40' }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center" style={{ color: modalidad.color }}>
                  {getPaymentIcon(modalidad.displayName)}
                  <span className="ml-2">{modalidad.displayName}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-2xl font-bold" style={{ color: modalidad.color }}>
                    S/ {modalidad.ventas.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-600">
                    {modalidad.transacciones} transacciones
                  </div>
                  <div className="text-sm font-medium" style={{ color: modalidad.color }}>
                    {modalidad.percentage}% del total
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Total general */}
      {summary && (
        <Card className="border-gray-300 bg-gray-50">
          <CardContent className="pt-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-lg font-semibold text-gray-900">Total General</p>
                <p className="text-sm text-gray-600">{summary.totalTransactions} transacciones</p>
              </div>
              <div className="text-3xl font-bold text-gray-900">
                S/ {summary.totalRevenue?.toLocaleString('es-PE', { minimumFractionDigits: 2 }) || '0.00'}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gráfico de distribución por modalidad */}
      {paymentMethods.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Distribución por Modalidad</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentMethods}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ displayName, percentage }) => `${displayName}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="ventas"
                  >
                    {paymentMethods.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`S/ ${value}`, 'Ventas']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-lg">Cargando datos del reporte...</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !error && paymentMethods.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <div className="text-lg font-medium text-gray-900">No hay datos de pagos</div>
              <div className="text-sm text-gray-500">Selecciona filtros y genera el reporte para ver la distribución por modalidad de pago</div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};